import { useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { BiEdit } from "react-icons/bi";
import { MdCheck, MdClose } from "react-icons/md";
import Permission from "../../userPermission/userPermission";
import { TONE, useEditSlot } from "../index";
import { useInlineSave } from "./useInlineSave";
import { AUTO_COMMIT_TYPES, INLINE_EDITORS } from "./InlineEditors";
import { validateInline } from "./validate";

/**
 * Enter/Escape are handled only for the editors where the key means nothing
 * else. Autocomplete and Select consume Enter to pick the highlighted option
 * and Escape to close their popup, and neither stops propagation — so handling
 * those keys here would commit the value from before the selection landed.
 */
const KEYBOARD_EDITORS = new Set(["text", "number"]);

/** Slots that render no label of their own, so the editor supplies one. */
const NEEDS_OWN_LABEL = new Set(["title", "subtitle"]);

/**
 * `<Permission permission={undefined}>` hides its children from everyone who
 * isn't an admin, so an unconfigured permission has to bypass the guard rather
 * than pass through it.
 */
const Guard = ({ permission, children }) =>
  permission ? (
    <Permission permission={permission}>{children}</Permission>
  ) : (
    children
  );

const EditableFieldInner = ({
  config,
  field,
  row,
  spec,
  display,
  label,
  variant,
}) => {
  const slot = useEditSlot();
  const { save, saving, fetchDetail } = useInlineSave(config);
  const [value, setValue] = useState(null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [opening, setOpening] = useState(false);
  const wrapperRef = useRef(null);

  const editing = slot.isEditing;

  // The keyboard covers the bottom of the viewport on phones; centring the
  // field keeps it visible once the editor expands. Guarded because jsdom and
  // older mobile browsers don't implement scrollIntoView.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!editing || typeof node?.scrollIntoView !== "function") return;
    const frame = requestAnimationFrame(() =>
      node.scrollIntoView({ block: "center", behavior: "smooth" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [editing]);

  const start = async () => {
    setError(null);
    setDetail(null);

    if (spec.hydrateOnOpen) {
      setOpening(true);
      try {
        const loaded = await fetchDetail(row);
        setDetail(loaded);
        setValue(
          spec.getValue ? spec.getValue(loaded ?? row, row) : loaded?.[field],
        );
      } catch (e) {
        setValue(spec.getValue ? spec.getValue(row, row) : row?.[field]);
      } finally {
        setOpening(false);
      }
    } else {
      setValue(spec.getValue ? spec.getValue(row, row) : row?.[field]);
    }

    slot.open();
  };

  const commit = async (nextValue = value) => {
    const message = validateInline(spec, nextValue, row);
    if (message) {
      setError(message);
      return;
    }
    const result = await save({ row, field, spec, value: nextValue, detail });
    if (result.ok) slot.close();
    else setError(result.message ?? null);
  };

  const cancel = () => {
    setError(null);
    slot.close();
  };

  if (!editing) {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          {display === null || display === undefined || display === ""
            ? "-"
            : display}
        </Box>
        <Guard permission={spec.permission ?? config.permission}>
          <IconButton
            size="small"
            aria-label={`تعديل ${spec.label ?? label ?? field}`}
            onClick={start}
            disabled={opening}
            sx={{ p: 0.35, color: TONE.brand, flexShrink: 0 }}
          >
            {opening ? <CircularProgress size={13} /> : <BiEdit size={15} />}
          </IconButton>
        </Guard>
      </Stack>
    );
  }

  const type = typeof spec.type === "function" ? spec.type(row) : spec.type;
  const Editor = INLINE_EDITORS[type ?? "text"] ?? INLINE_EDITORS.text;
  const autoCommit = AUTO_COMMIT_TYPES.has(type);

  return (
    <Box ref={wrapperRef} sx={{ width: "100%" }}>
      {NEEDS_OWN_LABEL.has(variant) && (spec.label ?? label) ? (
        <Typography sx={{ fontSize: 11, color: "#667085", mb: 0.5 }}>
          {spec.label ?? label}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={0.5} alignItems="flex-start">
        <Box
          sx={{ flex: 1, minWidth: 0 }}
          onKeyDown={(e) => {
            if (!KEYBOARD_EDITORS.has(type ?? "text")) return;
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Escape") cancel();
          }}
        >
          <Editor
            spec={spec}
            value={value}
            error={error}
            disabled={saving}
            onChange={(next) => {
              setValue(next);
              setError(null);
              if (autoCommit) commit(next);
            }}
          />
        </Box>
        {autoCommit ? null : (
          <>
            <IconButton
              size="small"
              aria-label="حفظ"
              disabled={saving}
              // iOS fires blur before click; preventing the default keeps the
              // input focused so the tap lands on the button.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => commit()}
              sx={{ p: 0.5, color: TONE.success }}
            >
              {saving ? <CircularProgress size={15} /> : <MdCheck size={17} />}
            </IconButton>
            <IconButton
              size="small"
              aria-label="إلغاء"
              disabled={saving}
              onMouseDown={(e) => e.preventDefault()}
              onClick={cancel}
              sx={{ p: 0.5, color: TONE.neutral }}
            >
              <MdClose size={17} />
            </IconButton>
          </>
        )}
      </Stack>
    </Box>
  );
};

/**
 * Wraps a card value so it can be edited in place, without opening the entity's
 * create/edit dialog.
 *
 * A field is editable only when the entity's config lists it — the whitelist is
 * the entity's create/edit form, because the backend PUT is full-replace and
 * would reject or silently clobber anything else.
 *
 * The outer component calls no hooks, so a non-editable field renders exactly
 * what it did before and works outside a Redux Provider.
 */
const EditableField = ({
  config,
  field,
  row,
  display,
  label,
  variant = "tile",
}) => {
  const spec = config?.fields?.[field];
  const allowed = spec && (config.canEdit ? config.canEdit(row) : true);

  // Returning null rather than an empty node lets MobileListItem apply its own
  // "-" placeholder in a tile, and lets the note box stay hidden entirely.
  if (!allowed) {
    return display === null || display === undefined || display === ""
      ? null
      : display;
  }

  return (
    <EditableFieldInner
      config={config}
      field={field}
      row={row}
      spec={spec}
      display={display}
      label={label}
      variant={variant}
    />
  );
};

export default EditableField;
